/**
 * SettingTooltip
 *
 * Accessible question-mark tooltip used next to admin setting labels.
 *
 * Behavior:
 *  - Shows on pointer hover and on keyboard focus.
 *  - Toggles on click/tap (mobile friendly).
 *  - Closes on Escape and on outside click.
 *  - Renders the setting `description` plus optional metadata (default value,
 *    range, options, "affects" hint) when provided.
 *
 * Accessibility:
 *  - The trigger is a real <button> with type="button", aria-label, and
 *    aria-expanded reflecting the open state.
 *  - The tooltip panel uses role="tooltip" and a stable id wired via
 *    aria-describedby so that screen readers associate it with the trigger.
 *
 * Dependencies:
 *  - None beyond React. We intentionally avoid adding a new tooltip library
 *    to keep the bundle small and the styling consistent with the existing
 *    dark admin theme.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import "./SettingTooltip.css";

export interface SettingTooltipConstraintOption {
  value: string | number;
  label: string;
}

export interface SettingTooltipConstraints {
  min?: number;
  max?: number;
  step?: number;
  options?: (string | number | SettingTooltipConstraintOption)[];
}

export interface SettingTooltipProps {
  /** Plain-text description shown inside the tooltip. */
  description?: string;
  /** Default value declared for the setting (for the metadata footer). */
  defaultValue?: unknown;
  /** Numeric or enum constraints declared for the setting. */
  constraints?: SettingTooltipConstraints;
  /** Optional human-readable hint about which subsystem this affects. */
  affects?: string;
  /** Optional aria-label override for the trigger button. */
  ariaLabel?: string;
  /** Optional inline style override for the trigger (positioning tweaks). */
  triggerStyle?: CSSProperties;
}

/**
 * Format an unknown default value into a stable string.
 *
 * Returns null when the value is undefined or an empty string so that the
 * caller can decide whether to render the metadata row at all.
 */
function formatDefaultValue(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === "string" && value.length === 0) {
    return null;
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return String(value);
}

/**
 * Build a "Range: min..max" style string when min and/or max are set.
 */
function formatRange(constraints?: SettingTooltipConstraints): string | null {
  if (!constraints) {
    return null;
  }
  const { min, max, step } = constraints;
  if (min === undefined && max === undefined) {
    return null;
  }
  const lo = min !== undefined ? String(min) : "−∞";
  const hi = max !== undefined ? String(max) : "+∞";
  return step !== undefined ? `${lo} – ${hi} (step ${step})` : `${lo} – ${hi}`;
}

/**
 * Build an "Options: a, b, c" string when select options are declared.
 */
function formatOptions(constraints?: SettingTooltipConstraints): string | null {
  const options = constraints?.options;
  if (!options || options.length === 0) {
    return null;
  }
  return options.map(String).join(", ");
}

export function SettingTooltip(props: SettingTooltipProps): JSX.Element | null {
  const {
    description,
    defaultValue,
    constraints,
    affects,
    ariaLabel,
    triggerStyle,
  } = props;

  // If there is nothing meaningful to show, render nothing at all so we don't
  // pollute the layout with an empty trigger.
  const defaultText = formatDefaultValue(defaultValue);
  const rangeText = formatRange(constraints);
  const optionsText = formatOptions(constraints);
  const hasMeta = Boolean(defaultText || rangeText || optionsText || affects);
  const hasContent = Boolean(description) || hasMeta;
  if (!hasContent) {
    return null;
  }

  const tooltipId = useId();
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Close on Escape when the tooltip is open and the trigger has focus.
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (event.key === "Escape" && isOpen) {
        event.stopPropagation();
        close();
      }
    },
    [isOpen, close],
  );

  // Close on outside click (helpful for click-to-open mobile interactions).
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const node = containerRef.current;
      if (!node) {
        return;
      }
      const target = event.target as Node | null;
      if (target && !node.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen]);

  return (
    <span
      ref={containerRef}
      className="setting-tooltip"
      onMouseEnter={open}
      onMouseLeave={close}
    >
      <button
        type="button"
        className="setting-tooltip__trigger"
        aria-label={ariaLabel ?? "More information about this setting"}
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-expanded={isOpen}
        onClick={toggle}
        onFocus={open}
        onBlur={close}
        onKeyDown={handleKeyDown}
        style={triggerStyle}
      >
        <span aria-hidden="true">?</span>
      </button>

      {isOpen && (
        <span
          id={tooltipId}
          role="tooltip"
          className="setting-tooltip__panel"
        >
          {description && (
            <span className="setting-tooltip__description">{description}</span>
          )}
          {hasMeta && (
            <span className="setting-tooltip__meta">
              {defaultText !== null && (
                <span className="setting-tooltip__meta-row">
                  <span className="setting-tooltip__meta-key">Default:</span>
                  <span className="setting-tooltip__meta-value">
                    {defaultText}
                  </span>
                </span>
              )}
              {rangeText && (
                <span className="setting-tooltip__meta-row">
                  <span className="setting-tooltip__meta-key">Range:</span>
                  <span className="setting-tooltip__meta-value">
                    {rangeText}
                  </span>
                </span>
              )}
              {optionsText && (
                <span className="setting-tooltip__meta-row">
                  <span className="setting-tooltip__meta-key">Options:</span>
                  <span className="setting-tooltip__meta-value">
                    {optionsText}
                  </span>
                </span>
              )}
              {affects && (
                <span className="setting-tooltip__meta-row">
                  <span className="setting-tooltip__meta-key">Affects:</span>
                  <span className="setting-tooltip__meta-value">{affects}</span>
                </span>
              )}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

export default SettingTooltip;
